'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { assertNever } from '@/lib/typescript/assertNever'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Divider } from '@/ui/layouts/divider/Divider'
import { Link } from '@/ui/links/link/Link.client'
import { Notification } from '@/ui/notification/Notification'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { useCallback, useMemo, type ComponentProps, type ReactElement, type ReactNode } from 'react'
import type { Key } from 'react-aria'
import { useFocusRing } from 'react-aria'
import { Tabs as AriaTabs, Collection, Tab, TabList, TabPanel } from 'react-aria-components'
import { useTabsScroll } from './_hooks/useTabsScroll'

type TabDefinitionBase = {
  id: string
  label: ReactNode
  icon?: ReactElement
  disabled?: boolean
}

export type TabItemDefinition = TabDefinitionBase & {
  content: ReactNode
  badge?: number
  onClick?: () => void
}

export type TabLinkDefinition = TabDefinitionBase & {
  href: string
  opensInNewTab?: boolean
}

export type TabDefinition = TabItemDefinition | TabLinkDefinition

export function isTabLink(tab: TabDefinition): tab is TabLinkDefinition {
  return 'href' in tab
}

export function isTabItem(tab: TabDefinition): tab is TabItemDefinition {
  return 'content' in tab
}

export const tabUnderlineThickness = 'border-b-px hd:border-b-1.5'

export const tabsBaselineDividerClassName = 'pointer-events-none absolute inset-x-0 bottom-0 z-0'

const tabItemCva = cva('relative z-[1] border-solid', {
  variants: {
    variant: {
      md: '',
      sm: '',
    },
    selected: {
      true: `${tabUnderlineThickness} border-brand-1-700 text-brand-1-700`,
      false: 'text-brand-2-900',
    },
    disabled: {
      true: 'border-b-0 text-brand-2-400 group-data-[hovered]:border-b-0',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'md',
      selected: false,
      className: `${tabUnderlineThickness} border-brand-2-200 group-data-[hovered]:border-brand-1-700`,
    },
    {
      variant: 'sm',
      selected: false,
      className: 'border-b-0 group-data-[hovered]:border-b-0',
    },
  ],
})

const tabTriggerClassName =
  'group flex cursor-pointer items-stretch whitespace-nowrap outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-1-700 data-[focus-visible]:ring-offset-2'

const tabContentCva = cva('relative flex items-center gap-1 uppercase leading-light', {
  variants: {
    variant: {
      md: 'py-2 font-bold',
      sm: 'font-medium',
    },
    selected: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'sm',
      selected: true,
      className: 'font-bold',
    },
  ],
})

type TabTriggerProps = ComponentProps<typeof Tab>

function TabTrigger({ children, className, ...props }: TabTriggerProps) {
  const { focusProps, isFocusVisible } = useFocusRing()

  return (
    <Tab
      {...props}
      {...focusProps}
      data-focus-visible={isFocusVisible || undefined}
      className={twMerge(tabTriggerClassName, className)}
    >
      {children}
    </Tab>
  )
}

type TabsItemVariantProps = VariantProps<typeof tabItemCva>

export type TabsItemProps = TabsItemVariantProps & {
  tab: TabItemDefinition
  icon?: ReactElement
}

export function TabsItem({ tab, selected, variant, icon }: TabsItemProps) {
  const { t } = useTranslation(['tabs'])
  const disabled = tab.disabled ?? false

  return (
    <TabTrigger id={tab.id} isDisabled={disabled} data-testid={`tabs/item/${tab.id}`}>
      <div
        className={twMerge(
          tabContentCva({ variant, selected }),
          tabItemCva({ variant, selected, disabled }),
        )}
      >
        {icon}
        <span className="relative">
          {tab.label}
          {tab.badge !== undefined && (
            <Notification
              number={tab.badge}
              aria-label={t('tabs:badgeCount', { count: tab.badge })}
              className="absolute -right-2 -top-1"
            />
          )}
        </span>
      </div>
    </TabTrigger>
  )
}

export type TabsLinkProps = TabsItemVariantProps & {
  tab: TabLinkDefinition
  icon?: ReactElement
}

export function TabsLink({ tab, selected, variant, icon }: TabsLinkProps) {
  const disabled = tab.disabled ?? false

  return (
    <TabTrigger id={tab.id} isDisabled={disabled} data-testid={`tabs/link/${tab.id}`}>
      <Link
        variant="free"
        underlined="none"
        href={tab.href}
        icon={icon}
        opensInNewTab={tab.opensInNewTab ?? false}
        bold={Boolean(!disabled && (variant === 'md' || selected))}
        className={twMerge(
          tabContentCva({ variant, selected }),
          tabItemCva({ variant, selected, disabled }),
          'data-[hovered]:decoration-transparent flex size-full no-underline data-[hovered]:no-underline',
        )}
      >
        {tab.label}
      </Link>
    </TabTrigger>
  )
}

export type TabsProps = StrictOmit<UiComponentProps, 'aria-label'> & {
  /** Visual variant of the tabs */
  variant: 'md' | 'sm'

  /** Accessible name for the tab list */
  'aria-label': string

  /** Tabs to display */
  tabs: TabDefinition[]

  /** Id of the currently selected tab */
  selectedTabId?: string

  /** Callback when the selected tab changes */
  onTabChange?: (tabId: string | undefined) => void

  /** Whether the user can unselect the active tab */
  allowUnselect?: boolean

  /** Optional buttons displayed next to the tab list */
  actions?: ReactElement<typeof Button>[]

  /** Enables horizontal scrolling with previous/next arrows */
  scrollable?: boolean
}

export function Tabs({
  variant,
  tabs,
  'aria-label': ariaLabel,
  className,
  id,
  'data-testid': dataTestId,
  selectedTabId,
  onTabChange,
  allowUnselect = false,
  actions,
  scrollable = false,
}: TabsProps) {
  const { t } = useTranslation(['CSSSlider'])
  const itemTabs = useMemo(() => tabs.filter(isTabItem), [tabs])

  // When allowUnselect=false and no explicit selection, default to first tab so the
  // active border and react-aria's selectedKey are always in sync from the first render.
  const effectiveSelectedTabId = useMemo(
    () =>
      !allowUnselect && selectedTabId === undefined
        ? (tabs[0]?.id ?? selectedTabId)
        : selectedTabId,
    [allowUnselect, selectedTabId, tabs],
  )

  // React Aria requires an active tab. Control its fallback key while the menu is
  // closed so initialization cannot emit a selection change that opens the menu.
  const ariaSelectedTabId =
    effectiveSelectedTabId ?? tabs.find((tab) => !tab.disabled)?.id ?? tabs[0]?.id ?? null

  const {
    ref: tabListRef,
    canScrollLeft,
    canScrollRight,
    scroll: scrollTabs,
  } = useTabsScroll(scrollable)

  const handleSelectionChange = useCallback(
    (key: Key) => {
      const nextTabId = key === null ? undefined : String(key)
      const currentSelectedTab = selectedTabId === nextTabId ? undefined : nextTabId

      if (currentSelectedTab === undefined && !allowUnselect) {
        return
      }

      onTabChange?.(currentSelectedTab)
      itemTabs.find((tab) => tab.id === currentSelectedTab)?.onClick?.()
    },
    [allowUnselect, itemTabs, onTabChange, selectedTabId],
  )

  return (
    <AriaTabs
      id={id}
      className={twMerge('flex w-full flex-col', className)}
      data-testid={dataTestId ?? 'tabs'}
      selectedKey={ariaSelectedTabId}
      onSelectionChange={handleSelectionChange}
    >
      <div className="relative flex w-full items-center gap-x-2">
        {canScrollLeft && (
          <Button
            size="md"
            isRound
            variant="tertiary"
            iconLeft={<Icon.ArrowLeft />}
            onPress={() => scrollTabs('left')}
            aria-label={t('CSSSlider:actions.previous.ariaLabel')}
            className="shrink-0 touch:hidden"
          >
            {t('CSSSlider:actions.previous.title')}
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <TabList
            ref={scrollable ? tabListRef : undefined}
            aria-label={ariaLabel}
            className={twMerge(
              'flex gap-4 !overflow-clip scroll-snap-x overflow-clip-margin-1',
              scrollable && '!overflow-x-auto !overflow-y-visible scroll-smooth scrollbar-none',
            )}
          >
            {tabs.map((tab) => {
              if (isTabLink(tab)) {
                return (
                  <TabsLink
                    tab={tab}
                    key={tab.id}
                    icon={tab.icon}
                    variant={variant}
                    selected={effectiveSelectedTabId === tab.id}
                  />
                )
              }

              if (isTabItem(tab)) {
                return (
                  <TabsItem
                    tab={tab}
                    key={tab.id}
                    selected={effectiveSelectedTabId === tab.id}
                    icon={tab.icon}
                    variant={variant}
                  />
                )
              }

              assertNever(tab)
            })}
          </TabList>
        </div>
        {canScrollRight && (
          <Button
            size="md"
            isRound
            variant="tertiary"
            iconLeft={<Icon.ArrowRight />}
            onPress={() => scrollTabs('right')}
            aria-label={t('CSSSlider:actions.next.ariaLabel')}
            className="shrink-0 touch:hidden"
          >
            {t('CSSSlider:actions.next.title')}
          </Button>
        )}
        {actions && actions.length > 0 && (
          <div className="flex shrink-0" data-testid="tabs-actions">
            {actions}
          </div>
        )}
        {variant === 'md' && (
          <Divider
            direction="horizontal"
            variant="line"
            aria-hidden
            className={tabsBaselineDividerClassName}
          />
        )}
      </div>
      <Collection
        items={itemTabs.map(({ id: tabId, content }) => ({
          id: tabId,
          content,
        }))}
      >
        {(item) => <TabPanel>{item.content}</TabPanel>}
      </Collection>
    </AriaTabs>
  )
}
